import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { io } from 'socket.io-client';
import { busesAPI, routesAPI, tripSessionsAPI, studentsAPI, attendanceAPI } from '../../services/api';

const SIMULATED_ROUTE = [
  { latitude: 48.8566, longitude: 2.3522, label: 'Depot Start' },
  { latitude: 48.8575, longitude: 2.3540, label: '' },
  { latitude: 48.8590, longitude: 2.3565, label: '' },
  { latitude: 48.8605, longitude: 2.3590, label: 'Stop 1 - Little One (pickup)', isPickup: 0 },
  { latitude: 48.8615, longitude: 2.3600, label: '' },
  { latitude: 48.8625, longitude: 2.3615, label: '' },
  { latitude: 48.8640, longitude: 2.3630, label: 'Stop 2 - School (dropoff)', isDropoff: 0 },
  { latitude: 48.8655, longitude: 2.3650, label: '' },
  { latitude: 48.8670, longitude: 2.3670, label: '' },
  { latitude: 48.8685, longitude: 2.3690, label: 'Return leg' },
  { latitude: 48.8695, longitude: 2.3700, label: '' },
  { latitude: 48.8700, longitude: 2.3710, label: '' },
  { latitude: 48.8705, longitude: 2.3715, label: 'Depot End' },
];

const GPS_ACCURACY_THRESHOLD = 30;

function DriverPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [isConnected, setIsConnected] = useState(false);
  const [isDriving, setIsDriving] = useState(false);
  const [gpsMode, setGpsMode] = useState('real');
  const [currentStep, setCurrentStep] = useState(0);
  const [routeId, setRouteId] = useState('');
  const [busId, setBusId] = useState('');
  const [buses, setBuses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [tripSessionId, setTripSessionId] = useState(null);
  const [routeStudents, setRouteStudents] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [studentStatus, setStudentStatus] = useState({});
  const [realPosition, setRealPosition] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [gpsAcquiring, setGpsAcquiring] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentLocations, setStudentLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const intervalRef = useRef(null);
  const geoWatchRef = useRef(null);
  const socketRef = useRef(null);
  const tripIdRef = useRef(null);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [b, r] = await Promise.all([busesAPI.list(), routesAPI.list()]);
        setBuses(b.data);
        setRoutes(r.data);
        if (r.data.length > 0) setRouteId(r.data[0].id);
        if (b.data.length > 0) setBusId(b.data[0].id);
      } catch (err) { /* ignore */ }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (!routeId) return;
    async function loadStudents() {
      try {
        const [sRes, aRes] = await Promise.all([
          studentsAPI.listByRoute(routeId),
          absencesAPI.list()
        ]);
        setRouteStudents(sRes.data);
        setAbsences(aRes.data);
        const status = {};
        sRes.data.forEach((s) => { status[s.id] = 'pending'; });
        setStudentStatus(status);
      } catch (err) { setRouteStudents([]); }
    }
    loadStudents();
  }, [routeId]);

  const handleConnect = () => {
    if (socketRef.current) socketRef.current.disconnect();
    const newSocket = io(window.location.origin, {
      auth: { tenantId: user?.tenant_id }
    });
    newSocket.on('connect', () => setIsConnected(true));
    newSocket.on('disconnect', () => {
      setIsConnected(false);
      setIsDriving(false);
      clearInterval(intervalRef.current);
      stopGeoWatch();
    });
    socketRef.current = newSocket;
  };

  const sendGpsUpdate = useCallback((lat, lng, speed) => {
    if (!socketRef.current) return;
    socketRef.current.emit('driver_location_update', {
      tenant_id: user?.tenant_id,
      bus_id: busId,
      route_id: routeId,
      trip_session_id: tripIdRef.current,
      latitude: lat,
      longitude: lng,
      speed: speed || 0,
      timestamp: new Date().toISOString()
    });
  }, [user?.tenant_id, busId, routeId]);

  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    try {
      const { data } = await studentsAPI.listLocations(student.id);
      // Combine main address with additional locations
      const allLocs = [
        { 
          id: 'primary', 
          label: t('primaryAddress'), 
          address: student.home_address, 
          latitude: student.home_location?.y, 
          longitude: student.home_location?.x 
        },
        ...data.map(l => ({
          id: l.id,
          label: l.label,
          address: l.address,
          latitude: l.latitude,
          longitude: l.longitude
        }))
      ];
      setStudentLocations(allLocs);
      setShowLocationModal(true);
    } catch (err) {
      console.error('Failed to load student locations');
    }
  };

  const handleActionWithLocation = (type, loc) => {
    if (!selectedStudent) return;
    logAttendance(selectedStudent.id, type, loc.latitude, loc.longitude);
    setShowLocationModal(false);
  };

  const logAttendance = async (studentId, eventType, lat, lng) => {
    const tid = tripIdRef.current;
    if (!tid) return;
    try {
      await attendanceAPI.create({
        trip_session_id: tid,
        student_id: studentId,
        event_type: eventType,
        latitude: lat,
        longitude: lng,
      });
      setStudentStatus((prev) => ({ ...prev, [studentId]: eventType }));
    } catch (err) {
      console.error('Attendance log failed:', err.message);
    }
  };

  const stopGeoWatch = useCallback(() => {
    if (geoWatchRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }
  }, []);

  const startSimulation = useCallback(() => {
    setCurrentStep(0);
    sendGpsUpdate(SIMULATED_ROUTE[0].latitude, SIMULATED_ROUTE[0].longitude, 35);
    let step = 1;
    intervalRef.current = setInterval(() => {
      if (step < SIMULATED_ROUTE.length) {
        setCurrentStep(step);
        sendGpsUpdate(SIMULATED_ROUTE[step].latitude, SIMULATED_ROUTE[step].longitude, 35);

        const point = SIMULATED_ROUTE[step];
        if (point.isPickup !== undefined && routeStudents.length > point.isPickup) {
          const student = routeStudents[point.isPickup];
          logAttendance(student.id, 'pickup', point.latitude, point.longitude);
        }
        if (point.isDropoff !== undefined && routeStudents.length > point.isDropoff) {
          const student = routeStudents[point.isDropoff];
          logAttendance(student.id, 'dropoff', point.latitude, point.longitude);
        }
        step++;
      } else {
        stopSimulation();
      }
    }, 3000);
  }, [routeStudents, sendGpsUpdate, stopGeoWatch]);

  const stopSimulation = useCallback(() => {
    clearInterval(intervalRef.current);
    const tid = tripIdRef.current;
    if (tid) {
      tripSessionsAPI.end(tid).catch(() => {});
    }
    setIsDriving(false);
    setTripSessionId(null);
    tripIdRef.current = null;
  }, []);

  const noSleepVideoRef = useRef(null);
  const wakeLockActiveRef = useRef(false);

  const acquireWakeLock = async () => {
    wakeLockActiveRef.current = true;
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
          if (wakeLockActiveRef.current) {
            navigator.wakeLock.request('screen').then((lock) => {
              wakeLockRef.current = lock;
            }).catch(() => {});
          }
        });
      } catch (err) { /* use video fallback */ }
    }
    if (!wakeLockRef.current) {
      try {
        const video = document.createElement('video');
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('loop', '');
        video.preload = 'auto';
        video.src = 'data:video/webm;base64,GkXfo59ChoEBQveBAULygQRC84EIQoKEd2VibUKHgQJChYECGFOAZwH/////////FUmpZpkq17GDD0JATYCGQ2hyb21lOITIhNjE1qBHVFJZQV9AAAAAAAABaGFzaGVyX1VTRVJfUkVGT1JUU9Cn2tb4o9Cn2tb4kIdJT05fQ0FOTk9UX0FORFJPSURfQU1JVENIIHw8Q1JFU0VMVEVEqH1QaXBlX1BBVFNIX1dFQkJJTkdDVkFOX1ZDQ19NQUlOIE5vbmNlIEV4cGlyeT1bXHhGRDNEQ0E5LTM1NDI1OTY2NjI4N11NQVJLRVJfQVVESU89W3hGRDNEMUIzOC0yNDQxODI2OTY5NDY1XX5vd1VTRVJfQUdFTlRfUEFSQU1fU1RZTEVfQU5EUk9JRF9DT01NRU5UX05vbmNlIFJlc3VsdD0wOk1hdHJpeF9BdWRpb19UWVBFPTAKZG9uZQ==';
        document.body.appendChild(video);
        video.play().catch(() => {});
        noSleepVideoRef.current = video;
      } catch (e) { /* no fallback available */ }
    }
  };

  const releaseWakeLock = async () => {
    wakeLockActiveRef.current = false;
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch (e) { /* ignore */ }
      wakeLockRef.current = null;
    }
    if (noSleepVideoRef.current) {
      try {
        noSleepVideoRef.current.pause();
        noSleepVideoRef.current.src = '';
        noSleepVideoRef.current.remove();
      } catch (e) { /* ignore */ }
      noSleepVideoRef.current = null;
    }
  };

  const startGeoWatchPrecise = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('gpsNotSupported')));
        return;
      }
      setGpsAcquiring(true);
      setGpsError('');
      let resolved = false;
      geoWatchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude, accuracy, speed: spd } = pos.coords;
          setRealPosition({ latitude, longitude, accuracy });
          sendGpsUpdate(latitude, longitude, spd || 0);
          if (!resolved && accuracy <= GPS_ACCURACY_THRESHOLD) {
            resolved = true;
            setGpsAcquiring(false);
            resolve({ latitude, longitude, accuracy });
          }
        },
        (err) => {
          if (!resolved) {
            resolved = true;
            setGpsAcquiring(false);
            reject(err);
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
      );
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          setGpsAcquiring(false);
          reject(new Error(t('gpsTimeout')));
        }
      }, 30000);
    });
  };

  const toggleDrive = async () => {
    if (isDriving) {
      stopGeoWatch();
      stopSimulation();
      await releaseWakeLock();
    } else {
      try {
        if (gpsMode === 'real') {
          setGpsAcquiring(true);
          const pos = await startGeoWatchPrecise();
          const { data } = await tripSessionsAPI.create({ route_id: routeId, driver_id: user?.id });
          setTripSessionId(data.id);
          tripIdRef.current = data.id;
          setIsDriving(true);
          sendGpsUpdate(pos.latitude, pos.longitude, 0);
          await acquireWakeLock();
        } else {
          const { data } = await tripSessionsAPI.create({ route_id: routeId, driver_id: user?.id });
          setTripSessionId(data.id);
          tripIdRef.current = data.id;
          setIsDriving(true);
          startSimulation();
          await acquireWakeLock();
        }
      } catch (err) {
        setGpsAcquiring(false);
        stopGeoWatch();
        if (err.message === t('gpsNotSupported') || err.message === t('gpsTimeout') || err.code) {
          setGpsError(err.code === 1 ? t('gpsPermissionDenied') : err.message || t('gpsError'));
        } else {
          alert('Failed to create trip session.');
        }
      }
    }
  };

  useEffect(() => () => {
    clearInterval(intervalRef.current);
    stopGeoWatch();
    releaseWakeLock();
  }, [stopGeoWatch]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isDriving && wakeLockActiveRef.current && !wakeLockRef.current) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (e) { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDriving]);

  const [view, setView] = useState('dashboard'); // 'dashboard', 'config', 'trip'

  const handleLogout = () => {
    if (socketRef.current) socketRef.current.disconnect();
    clearInterval(intervalRef.current);
    stopGeoWatch();
    releaseWakeLock();
    logout();
    navigate('/login');
  };

  const statusLabel = (s) => {
    if (s === 'pickup') return { text: t('pickedUp'), color: '#28a745', bg: '#d4edda' };
    if (s === 'dropoff') return { text: t('droppedOff'), color: '#007bff', bg: '#d1ecf1' };
    return { text: t('pending'), color: '#6c757d', bg: '#f8f9fa' };
  };

  // Render Dashboard Home
  if (view === 'dashboard') {
    return (
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', backgroundColor: '#fff', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h1 style={{ margin: 0, fontSize: '20px' }}>🚌 {t('driverApp')}</h1>
          <button onClick={handleLogout} style={{ padding: '8px 16px', background: '#fee2e2', color: '#c53030', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>{t('logout')}</button>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
          <button 
            onClick={() => setView('config')}
            style={{ padding: '30px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'left', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}
          >
            ⚙️ {t('setupBusAndRoute')}
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#718096', fontWeight: 'normal' }}>{t('selectVehicleAndPath')}</p>
          </button>

          <button 
            onClick={() => setView('trip')}
            disabled={!busId || !routeId}
            style={{ padding: '30px', background: (!busId || !routeId) ? '#edf2f7' : '#48bb78', color: (!busId || !routeId) ? '#a0aec0' : '#fff', border: 'none', borderRadius: '20px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'left', boxShadow: '0 4px 12px rgba(72, 187, 120, 0.2)' }}
          >
            🚀 {isDriving ? t('continueTrip') : t('startNewTrip')}
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: isDriving ? '#fff' : '#718096', fontWeight: 'normal', opacity: 0.9 }}>{t('manageStudentsAndGps')}</p>
          </button>
        </div>
      </div>
    );
  }

  // Render Sub-pages
  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '24px' }}>
        <button onClick={() => setView('dashboard')} style={{ padding: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⬅️</button>
        <h1 style={{ margin: 0, fontSize: '20px' }}>{view === 'config' ? t('configuration') : t('tripControls')}</h1>
      </header>

      {view === 'config' && (
        <section style={{ marginBottom: '24px' }}>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #edf2f7' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginBottom: '15px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#718096' }}>
                {t('bus')}
                <select value={busId} onChange={(e) => setBusId(e.target.value)} disabled={isDriving} style={{ width: '100%', padding: '12px', marginTop: '4px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f7fafc' }}>
                  {buses.map((b) => <option key={b.id} value={b.id}>{b.bus_number}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#718096' }}>
                {t('route')}
                <select value={routeId} onChange={(e) => setRouteId(e.target.value)} disabled={isDriving} style={{ width: '100%', padding: '12px', marginTop: '4px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f7fafc' }}>
                  {routes.map((r) => <option key={r.id} value={r.id}>{r.route_name}</option>)}
                </select>
              </label>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#718096', marginBottom: '8px', display: 'block' }}>{t('gpsMode')}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => !isDriving && setGpsMode('real')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid', borderColor: gpsMode === 'real' ? '#48bb78' : '#e2e8f0', background: gpsMode === 'real' ? '#f0fff4' : '#fff', color: gpsMode === 'real' ? '#2f855a' : '#718096', fontWeight: 'bold' }}>🛰️ {t('realGps')}</button>
                <button onClick={() => !isDriving && setGpsMode('simulated')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid', borderColor: gpsMode === 'simulated' ? '#4299e1' : '#e2e8f0', background: gpsMode === 'simulated' ? '#ebf8ff' : '#fff', color: gpsMode === 'simulated' ? '#2b6cb0' : '#718096', fontWeight: 'bold' }}>🎮 {t('simulatedGps')}</button>
              </div>
            </div>

            <button onClick={handleConnect} style={{ width: '100%', padding: '12px', background: isConnected ? '#48bb78' : '#4299e1', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>
              {isConnected ? '✅ ' + t('connected') : '🔌 ' + t('connectToServer')}
            </button>
          </div>
        </section>
      )}

      {view === 'trip' && (
        <>
          <section style={{ marginBottom: '24px' }}>
            <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #edf2f7', textAlign: 'center' }}>
              <button onClick={toggleDrive} disabled={!isConnected || !busId || !routeId || gpsAcquiring} style={{ width: '100%', padding: '16px', background: isDriving ? '#e53e3e' : '#48bb78', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '18px', cursor: 'pointer' }}>
                {gpsAcquiring ? '⏳ ' + t('acquiringGps') : (isDriving ? '🛑 ' + t('endTrip') : '🚀 ' + t('startTrip'))}
              </button>
            </div>
          </section>

          {routeStudents.length > 0 && (
            <section>
              <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#4a5568' }}>👥 {t('studentsOnRoute', { n: routeStudents.length })}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {routeStudents.map((student) => {
                  const isAbsent = absences.some(a => a.student_id === student.id && new Date(a.date).toDateString() === new Date().toDateString());
                  const st = isAbsent ? { text: t('absent'), color: '#dc3545', bg: '#f8d7da' } : statusLabel(studentStatus[student.id]);
                  return (
                    <div 
                      key={student.id} 
                      style={{ background: isAbsent ? '#fff5f5' : '#fff', padding: '16px', borderRadius: '10px', border: isAbsent ? '2px solid #feb2b2' : '2px solid #e0e0e0', opacity: isAbsent ? 0.8 : 1, cursor: 'pointer' }}
                      onClick={() => handleStudentClick(student)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#eee', overflow: 'hidden', flexShrink: 0 }}>
                          <img src={student.photo_url ? (student.photo_url.startsWith('http') ? student.photo_url : `/api${student.photo_url}`) : 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{student.first_name} {student.last_name}</strong>
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: st.color }}>{st.text}</span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#718096' }}>📍 {student.home_address}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {showLocationModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '100%', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>{selectedStudent?.first_name} {selectedStudent?.last_name}</h2>
              <button onClick={() => setShowLocationModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            {studentLocations.map(loc => (
              <div key={loc.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                <div style={{ fontWeight: 'bold' }}>{loc.label}</div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>{loc.address}</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleActionWithLocation('pickup', loc)} style={{ flex: 1, padding: '8px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px' }}>{t('pickup')}</button>
                  <button onClick={() => handleActionWithLocation('dropoff', loc)} style={{ flex: 1, padding: '8px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '4px' }}>{t('dropoff')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DriverPanel;
